/**
 * Countdown
 *
 * PHP renders the complete markup (fields, labels, current digits) and the
 * target as an absolute timestamp, so no timezone math happens here. This
 * script only rewrites digits: each changed digit rolls out while the new
 * one rolls in (Web Animations API, no GSAP), and the singular / plural
 * label is toggled. Text is never re-cased.
 */

var IS_BUILDER = window.location.href.includes("brickspreview");

// Unit sizes in seconds, largest first (same as the PHP side).
var UNITS = [
    ["d", 86400],
    ["h", 3600],
    ["m", 60],
    ["s", 1],
];

var FALLBACK_EASING = "ease-out";

function parseJson(raw) {
    if (!raw) return {};
    try {
        return JSON.parse(raw) || {};
    } catch (e) {
        return {};
    }
}

function isValidEasing(easing) {
    try {
        document.createElement("div").animate([], { easing: easing });
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Remaining seconds split into the used units. A unit is taken modulo the next
 * larger used unit, so missing units carry over (no %D → total hours).
 */
function splitRemaining(remaining, usedUnits) {
    var values = {};
    var parent = null;
    UNITS.forEach(function (entry) {
        var unit = entry[0];
        var size = entry[1];
        if (!usedUnits.has(unit)) return;
        var rest = parent === null ? remaining : remaining % parent;
        values[unit] = Math.floor(rest / size);
        parent = size;
    });
    return values;
}

function createDigit(char) {
    var digit = document.createElement("span");
    digit.className = "mad-countdown__digit";
    var current = document.createElement("span");
    current.className = "mad-countdown__digit-current";
    current.textContent = char;
    digit.appendChild(current);
    return digit;
}

class MadCountdown {
    constructor(root) {
        if (root._madCountdown) root._madCountdown.destroy();

        this.root = root;
        this.config = Object.assign(
            { target: 0, animation: "roll-down", duration: 400, easing: FALLBACK_EASING, fadeFrom: 0, reducedMotion: false },
            parseJson(root.dataset.madCountdown)
        );

        if (!this.config.target) return;

        if (!isValidEasing(this.config.easing)) this.config.easing = FALLBACK_EASING;

        if (
            this.config.reducedMotion &&
            window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
            this.config.animation = "none";
        }

        this.numbers = Array.from(
            root.querySelectorAll(":scope > .mad-countdown__field > .mad-countdown__value > .mad-countdown__number")
        );
        this.usedUnits = new Set(this.numbers.map((number) => number.dataset.unit));

        // Fields whose label has a singular form, keyed to the field's first number.
        this.labels = Array.from(root.querySelectorAll(":scope > .mad-countdown__field"))
            .map((field) => ({
                number: field.querySelector(":scope > .mad-countdown__value > .mad-countdown__number"),
                plural: field.querySelector(":scope > .mad-countdown__label > .mad-countdown__label-plural"),
                singular: field.querySelector(":scope > .mad-countdown__label > .mad-countdown__label-singular"),
            }))
            .filter((label) => label.number && label.plural && label.singular);

        root._madCountdown = this;

        // First paint without animation: the server-rendered digits may be
        // stale (page cache, server/client clock difference).
        this.tick(false);
    }

    tick(animate) {
        if (!this.root.isConnected) {
            this.destroy();
            return;
        }

        // Rounded up, so 0 is shown exactly when the target is reached.
        var diff = this.config.target - Date.now();
        var remaining = Math.max(0, Math.ceil(diff / 1000));
        var values = splitRemaining(remaining, this.usedUnits);

        this.numbers.forEach((number) => {
            var value = values[number.dataset.unit];
            var string = String(value);
            if (number.dataset.pad === "1") string = string.padStart(2, "0");
            this.updateNumber(number, string, animate);
        });

        this.labels.forEach((label) => {
            var isSingular = values[label.number.dataset.unit] === 1;
            label.plural.hidden = isSingular;
            label.singular.hidden = !isSingular;
        });

        if (remaining === 0) {
            this.root.classList.add("is-finished");
            return;
        }

        this.root.classList.remove("is-finished");

        // Wake up right after the next whole remaining second, so the display
        // never drifts against the clock.
        var delay = ((diff - 1) % 1000) + 1 + 20;
        this.timer = setTimeout(() => this.tick(true), delay);
    }

    updateNumber(number, string, animate) {
        var digits = Array.from(number.children);

        // Digit count changed (e.g. 100 → 99 days): rebuild without animation.
        if (digits.length !== string.length) {
            digits.forEach((digit) => digit._madDone && digit._madDone());
            number.replaceChildren.apply(number, Array.from(string).map(createDigit));
            return;
        }

        digits.forEach((digit, index) => {
            var char = string[index];
            if (digit._madTarget === undefined) {
                digit._madTarget = digit.firstElementChild.textContent;
            }
            if (digit._madTarget === char) return;
            this.updateDigit(digit, char, animate);
        });
    }

    updateDigit(digit, char, animate) {
        // A still-running roll is committed first.
        if (digit._madDone) digit._madDone();

        var current = digit.firstElementChild;
        digit._madTarget = char;

        if (!animate || this.config.animation === "none") {
            current.textContent = char;
            return;
        }

        var next = document.createElement("span");
        next.className = "mad-countdown__digit-next";
        next.textContent = char;
        digit.appendChild(next);

        var frames = this.getKeyframes();
        var timing = { duration: this.config.duration, easing: this.config.easing };
        var outAnimation = current.animate(frames.out, Object.assign({ fill: "forwards" }, timing));
        var inAnimation = next.animate(frames.in, timing);

        var done = function () {
            if (digit._madDone !== done) return;
            delete digit._madDone;
            outAnimation.cancel();
            inAnimation.cancel();
            current.textContent = char;
            next.remove();
        };
        digit._madDone = done;
        inAnimation.onfinish = done;
    }

    getKeyframes() {
        var from = this.config.fadeFrom;

        if (this.config.animation === "fade") {
            return {
                out: [{ opacity: 1 }, { opacity: 0 }],
                in: [{ opacity: 0 }, { opacity: 1 }],
            };
        }

        // roll-down: the new digit drops in from above; roll-up: rises from below.
        var sign = this.config.animation === "roll-up" ? -1 : 1;
        return {
            out: [
                { transform: "translateY(0)", opacity: 1 },
                { transform: "translateY(" + sign * 100 + "%)", opacity: from },
            ],
            in: [
                { transform: "translateY(" + -sign * 100 + "%)", opacity: from },
                { transform: "translateY(0)", opacity: 1 },
            ],
        };
    }

    destroy() {
        clearTimeout(this.timer);
        if (this.numbers) {
            this.numbers.forEach((number) => {
                Array.from(number.children).forEach((digit) => digit._madDone && digit._madDone());
            });
        }
        if (this.root._madCountdown === this) delete this.root._madCountdown;
    }
}

function setupMadCountdown() {
    document.querySelectorAll(".mad-countdown").forEach(function (root) {
        // Frontend: init once. Builder: always rebuild (Bricks calls this after a re-render).
        if (root._madCountdown && !IS_BUILDER) return;
        try {
            new MadCountdown(root);
        } catch (e) {
            console.error("[Countdown]", e);
        }
    });
}

window.setupMadCountdown = setupMadCountdown;

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setupMadCountdown);
} else {
    setupMadCountdown();
}
